// K8s GNB 메뉴 그룹핑 SSOT.
// route는 라우팅(path/권한)만, i18n은 번역만 담당하고, "어느 화면이 어느 그룹에 어떤 라벨로" 는 이 define이 소유한다.
// - item.value: route에서 생성되는 MenuInfo.value(`kubernetes_<path>`)를 참조하는 ID. path·권한은 baseMenu가 value로 MenuInfo에서 끌어온다.
// - item.label: 메뉴 표시용 WORD.GNB i18n 키(원문 아님 → 번역 유지).
// - 완전성 타입체크: 메뉴 대상 value(K8sMenuValue)가 하나라도 그룹에 안 담기면 컴파일 에러 → 화면 누락 원천 차단.

const PREFIX = 'kubernetes_';

// 메뉴에 노출되는 K8s 리프 화면의 value 목록(SSOT). value = `kubernetes_<route child path>`.
// 라우트 parent와 리다이렉트(deployments/statefulSets/... redirect), 성능분석(별도 소스)은 제외한다.
export const K8S_MENU_VALUES = [
  `${PREFIX}cluster-topology`,
  `${PREFIX}object-topology`,
  `${PREFIX}cluster`,
  `${PREFIX}pod`,
  `${PREFIX}container`,
  `${PREFIX}namespace`,
  `${PREFIX}node`,
  `${PREFIX}storage`,
  `${PREFIX}cluster-resource-inventory`,
  `${PREFIX}workload`,
  `${PREFIX}hpa`,
  `${PREFIX}job`,
  `${PREFIX}cron-job`,
  `${PREFIX}service-map`,
  `${PREFIX}service`,
  `${PREFIX}ingress`,
  `${PREFIX}ingress-controller`,
  `${PREFIX}route`,
  `${PREFIX}config-map`,
  `${PREFIX}persistent-volume-claim`,
  `${PREFIX}persistent-volume`,
  `${PREFIX}storage-class`,
  `${PREFIX}role`,
  `${PREFIX}role-binding`,
  `${PREFIX}cluster-role`,
  `${PREFIX}cluster-role-binding`,
  `${PREFIX}service-account`,
] as const;

export type K8sMenuValue = (typeof K8S_MENU_VALUES)[number];

// 통합 Workload 뷰(?type=)로 펼쳐지는 타입 탭. value는 kubernetes_workload 하나를 공유하고 type만 다르다.
export type K8sWorkloadType = 'deployments' | 'statefulSets' | 'daemonSets' | 'replicaSets';

interface K8sGnbLeaf {
  value: K8sMenuValue; // MenuInfo.value 참조 ID
  label: string; // WORD.GNB i18n 키
}

interface K8sGnbWorkloadTypes {
  value: Extract<K8sMenuValue, `${typeof PREFIX}workload`>; // 통합 workload
  types: readonly { type: K8sWorkloadType; label: string }[]; // ?type= 별 탭
}

interface K8sGnbSubGroup {
  subGroupLabel: string; // 서브그룹 라벨(예: Batch, OpenShift) — 앞에 구분선(|) 표시
  items: readonly K8sGnbLeaf[];
  requiresOpenShift?: boolean; // true면 OpenShift 클러스터 존재 시에만 노출(예: OpenShift Route)
}

export type K8sGnbItem = K8sGnbLeaf | K8sGnbWorkloadTypes | K8sGnbSubGroup;

// items를 readonly로 둬야 아래 `as const satisfies`의 리터럴 튜플을 수용한다(완전성 타입체크의 전제).
interface K8sGnbGroup {
  groupLabel: string; // 그룹 라벨(WORD.GNB 키)
  valueSuffix: string; // 그룹 value suffix (kubernetes_<suffix>)
  items: readonly K8sGnbItem[];
}

// ── 그룹 정의 (순서 = 메뉴 노출 순서) ──────────────────────────────
// `as const satisfies`: 리터럴(value)을 보존해야 아래 완전성 타입체크가 실제로 누락을 잡는다.
// 명시 타입 주석(`: K8sGnbGroup[]`)을 쓰면 리터럴이 인터페이스로 넓어져 MissingValue가 항상 never가 됨.
export const K8S_GNB_GROUPS = [
  {
    groupLabel: 'INFRASTRUCTURE',
    valueSuffix: 'infrastructure',
    items: [
      { value: `${PREFIX}cluster-topology`, label: 'CLUSTER_TOPOLOGY' },
      { value: `${PREFIX}cluster`, label: 'CLUSTER_OVERVIEW' },
      { value: `${PREFIX}node`, label: 'NODE' },
      { value: `${PREFIX}namespace`, label: 'NAMESPACE' },
      { value: `${PREFIX}pod`, label: 'POD' },
      { value: `${PREFIX}container`, label: 'CONTAINER' },
    ],
  },
  {
    groupLabel: 'WORKLOADS',
    valueSuffix: 'workloads',
    items: [
      {
        value: `${PREFIX}workload`,
        types: [
          { type: 'deployments', label: 'DEPLOYMENT' },
          { type: 'statefulSets', label: 'STATEFULSET' },
          { type: 'daemonSets', label: 'DAEMONSET' },
          { type: 'replicaSets', label: 'REPLICASET' },
        ],
      },
      {
        subGroupLabel: 'BATCH',
        items: [
          { value: `${PREFIX}job`, label: 'JOB' },
          { value: `${PREFIX}cron-job`, label: 'CRONJOB' },
        ],
      },
    ],
  },
  {
    groupLabel: 'NETWORK',
    valueSuffix: 'network',
    items: [
      { value: `${PREFIX}service-map`, label: 'SERVICE_MAP' },
      { value: `${PREFIX}service`, label: 'KUBE_SERVICE' },
      { value: `${PREFIX}ingress`, label: 'INGRESS' },
      { value: `${PREFIX}ingress-controller`, label: 'INGRESS_CONTROLLER' },
      {
        subGroupLabel: 'OPENSHIFT',
        requiresOpenShift: true,
        items: [{ value: `${PREFIX}route`, label: 'ROUTE' }],
      },
    ],
  },
  {
    groupLabel: 'STORAGE',
    valueSuffix: 'storage',
    items: [
      { value: `${PREFIX}storage`, label: 'STORAGE_OVERVIEW' },
      { value: `${PREFIX}persistent-volume-claim`, label: 'PERSISTENT_VOLUME_CLAIM' },
      { value: `${PREFIX}persistent-volume`, label: 'PERSISTENT_VOLUME' },
      { value: `${PREFIX}storage-class`, label: 'STORAGE_CLASSES' },
    ],
  },
  {
    groupLabel: 'SCALING',
    valueSuffix: 'scaling',
    items: [{ value: `${PREFIX}hpa`, label: 'HPA' }],
  },
  {
    groupLabel: 'ACCESS_CONTROL',
    valueSuffix: 'accessControl',
    items: [
      { value: `${PREFIX}role`, label: 'ROLE' },
      { value: `${PREFIX}role-binding`, label: 'ROLE_BINDING' },
      { value: `${PREFIX}cluster-role`, label: 'CLUSTER_ROLE' },
      { value: `${PREFIX}cluster-role-binding`, label: 'CLUSTER_ROLE_BINDING' },
      { value: `${PREFIX}service-account`, label: 'SERVICEACCOUNT' },
    ],
  },
  {
    groupLabel: 'CONFIGURATION',
    valueSuffix: 'configuration',
    items: [{ value: `${PREFIX}config-map`, label: 'CONFIGMAP' }],
  },
  {
    groupLabel: 'INVENTORY',
    valueSuffix: 'inventory',
    items: [
      { value: `${PREFIX}object-topology`, label: 'OBJECT_TOPOLOGY' },
      { value: `${PREFIX}cluster-resource-inventory`, label: 'CLUSTER_RESOURCE_INVENTORY' },
    ],
  },
] as const satisfies readonly K8sGnbGroup[];

// ── 완전성 타입체크 ────────────────────────────────────────────────
// 모든 K8sMenuValue가 그룹 어딘가에 담겼는지 컴파일타임 검증.
// 화면(route)을 추가하고 그룹에 안 넣으면 Missing이 never가 아니게 되어 아래 라인에서 타입 에러.
type LeafValue<T> = T extends { value: infer V; types: unknown[] }
  ? V
  : T extends { value: infer V }
    ? V
    : T extends { items: infer I extends readonly unknown[] }
      ? LeafValue<I[number]>
      : never;
type CoveredValue = LeafValue<(typeof K8S_GNB_GROUPS)[number]['items'][number]>;
type MissingValue = Exclude<K8sMenuValue, CoveredValue>;

const _exhaustiveCheck: MissingValue extends never
  ? true
  : ['메뉴 그룹에서 누락된 K8s value:', MissingValue] = true;
void _exhaustiveCheck;
